/**
 * Modal Component - Reusable modal dialog
 * Follows design system standards for user-facing pages
 */

import React, { useEffect, useRef } from 'react';
import { useBodyScrollLock, useEscapeKey } from '../hooks/useModalEffects';
import { X } from 'lucide-react';
import '../styles/Modal.css';

const Modal = ({ isOpen, onClose, title, children, size = 'md', maxWidth }) => {
  const modalRef = useRef();
  const previousActiveElement = useRef();

  // Use custom hooks for modal behavior
  useBodyScrollLock(isOpen);
  useEscapeKey(isOpen, onClose);

  // Handle focus management when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      // Store previous focus
      previousActiveElement.current = document.activeElement;

      // Focus modal
      setTimeout(() => modalRef.current?.focus(), 0);

      return () => {
        // Restore previous focus when modal closes
        previousActiveElement.current?.focus();
      };
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  // Focus trap implementation
  const handleTabKey = (e) => {
    if (e.key !== 'Tab') return;

    const focusableElements = modalRef.current?.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );

    if (!focusableElements || focusableElements.length === 0) return;

    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    if (e.shiftKey && document.activeElement === firstElement) {
      e.preventDefault();
      lastElement.focus();
    } else if (!e.shiftKey && document.activeElement === lastElement) {
      e.preventDefault();
      firstElement.focus();
    }
  };

  return (
    <div
      className="modal-overlay"
      onClick={handleOverlayClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby="modal-title"
    >
      <div
        ref={modalRef}
        className={`modal-content modal-${size}`}
        style={maxWidth ? { maxWidth } : undefined}
        tabIndex={-1}
        onKeyDown={handleTabKey}
      >
        <div className="modal-header">
          <h2 id="modal-title" className="modal-title">{title}</h2>
          <button
            className="modal-close"
            onClick={onClose}
            aria-label="Close modal"
          >
            <X size={24} />
          </button>
        </div>
        <div className="modal-body">
          {children}
        </div>
      </div>
    </div>
  );
};

// Comparison function: re-render when modal control props OR children change
const arePropsEqual = (prevProps, nextProps) => {
  if (
    prevProps.isOpen !== nextProps.isOpen ||
    prevProps.onClose !== nextProps.onClose ||
    prevProps.title !== nextProps.title ||
    prevProps.size !== nextProps.size
  ) {
    return false;
  }

  // If children changed (different reference), re-render to allow interactive children
  if (prevProps.children !== nextProps.children) return false;

  return true;
};

export default React.memo(Modal, arePropsEqual);
